import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  CalendarDays, 
  Plus, 
  Clock, 
  MapPin, 
  Phone, 
  User, 
  CheckCircle2, 
  Trash2,
  Pill,
  Syringe,
  Stethoscope,
  FlaskConical,
  CalendarCheck,
  MoreHorizontal
} from 'lucide-react';
import { toast } from 'sonner';
import API from '@/utils/api';
import AddAppointmentDialog from './AddAppointmentDialog';

const appointmentTypeConfig = {
  doctor_visit: { label: 'Doctor Visit', icon: Stethoscope, color: 'bg-emerald-100 text-emerald-700' },
  medication_refill: { label: 'Medication Refill', icon: Pill, color: 'bg-orange-100 text-orange-700' },
  vaccination: { label: 'Vaccination', icon: Syringe, color: 'bg-blue-100 text-blue-700' },
  lab_test: { label: 'Lab Test', icon: FlaskConical, color: 'bg-purple-100 text-purple-700' },
  follow_up: { label: 'Follow-up', icon: CalendarCheck, color: 'bg-red-100 text-red-700' },
  other: { label: 'Other', icon: MoreHorizontal, color: 'bg-gray-100 text-gray-700' },
};

const AppointmentsTab = () => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);

  useEffect(() => {
    fetchAppointments();
  }, [showCompleted]);

  const fetchAppointments = async () => {
    try {
      const response = await API.get(`/medical/appointments?include_completed=${showCompleted}`);
      setAppointments(response.data);
    } catch (error) {
      console.error('Failed to fetch appointments:', error);
      toast.error("Couldn't load appointments", {
        description: 'Please try refreshing the page'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async (id) => {
    try {
      await API.put(`/medical/appointments/${id}/complete`);
      toast.success('Appointment completed');
      fetchAppointments();
    } catch (error) {
      toast.error("Couldn't update appointment", {
        description: 'Please try again'
      });
    }
  };

  const handleDelete = async (id) => {
    try {
      await API.delete(`/medical/appointments/${id}`);
      toast.success('Appointment removed');
      fetchAppointments();
    } catch (error) {
      toast.error("Couldn't remove appointment", {
        description: 'Please try again'
      });
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (date.getTime() === today.getTime()) {
      return { text: 'Today', isUrgent: true };
    } else if (date.getTime() === tomorrow.getTime()) {
      return { text: 'Tomorrow', isUrgent: true };
    } else if (date < today) {
      return { text: 'Overdue', isOverdue: true };
    }
    
    return { 
      text: date.toLocaleDateString('en-US', { 
        weekday: 'short', 
        month: 'short', 
        day: 'numeric' 
      }), 
      isUrgent: false 
    };
  };

  const groupAppointmentsByDate = (appointments) => {
    const groups = {
      overdue: [],
      upcoming: [],
      later: [],
      completed: []
    };
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const nextWeek = new Date(today);
    nextWeek.setDate(nextWeek.getDate() + 7);
    
    appointments.forEach(appt => {
      if (appt.is_completed) {
        groups.completed.push(appt);
      } else {
        const apptDate = new Date(appt.date + 'T00:00:00');
        if (apptDate < today) {
          groups.overdue.push(appt);
        } else if (apptDate <= nextWeek) {
          groups.upcoming.push(appt);
        } else {
          groups.later.push(appt);
        }
      }
    });
    
    return groups;
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  const groupedAppointments = groupAppointmentsByDate(appointments);

  const AppointmentCard = ({ appointment }) => {
    const config = appointmentTypeConfig[appointment.appointment_type] || appointmentTypeConfig.other;
    const TypeIcon = config.icon;
    const dateInfo = formatDate(appointment.date);
    
    return (
      <Card className={`border-l-4 ${appointment.is_completed ? 'border-l-gray-300 bg-gray-50' : dateInfo.isOverdue ? 'border-l-red-500' : 'border-l-[#2E7D6A]'}`}>
        <CardContent className="p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3 flex-1">
              <div className={`p-2 rounded-lg ${config.color}`}>
                <TypeIcon className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className={`font-medium ${appointment.is_completed ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                    {appointment.title}
                  </h4>
                  <Badge variant="secondary" className={config.color}>
                    {config.label}
                  </Badge>
                </div>
                
                <div className="mt-2 space-y-1 text-sm text-gray-600">
                  <div className="flex items-center gap-4 flex-wrap">
                    <span className={`flex items-center gap-1 ${dateInfo.isOverdue ? 'text-red-600 font-medium' : dateInfo.isUrgent ? 'text-orange-600 font-medium' : ''}`}>
                      <CalendarDays className="h-3.5 w-3.5" />
                      {dateInfo.text}
                    </span>
                    {appointment.time && (
                      <span className="flex items-center gap-1">
                        <Clock className="h-3.5 w-3.5" />
                        {appointment.time}
                      </span>
                    )}
                  </div>
                  
                  {appointment.doctor_name && (
                    <div className="flex items-center gap-1">
                      <User className="h-3.5 w-3.5" />
                      {appointment.doctor_name}
                    </div>
                  )}
                  
                  {appointment.clinic_name && (
                    <div className="flex items-center gap-1">
                      <MapPin className="h-3.5 w-3.5" />
                      {appointment.clinic_name}
                      {appointment.clinic_address && ` - ${appointment.clinic_address}`}
                    </div>
                  )}
                  
                  {appointment.clinic_phone && (
                    <div className="flex items-center gap-1">
                      <Phone className="h-3.5 w-3.5" />
                      {appointment.clinic_phone}
                    </div>
                  )}
                  
                  {appointment.notes && (
                    <p className="text-gray-500 italic mt-1">{appointment.notes}</p>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-1 ml-2">
              {!appointment.is_completed && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleComplete(appointment.id)}
                  className="text-green-600 hover:text-green-700 hover:bg-green-50"
                  data-testid={`complete-appointment-${appointment.id}`}
                >
                  <CheckCircle2 className="h-4 w-4" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDelete(appointment.id)}
                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                data-testid={`delete-appointment-${appointment.id}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Appointments & Reminders</h3>
          <p className="text-sm text-gray-500">Track doctor visits, medication refills, and more</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowCompleted(!showCompleted)}
            className={showCompleted ? 'bg-gray-100' : ''}
          >
            {showCompleted ? 'Hide Completed' : 'Show Completed'}
          </Button>
          <Button
            onClick={() => setDialogOpen(true)}
            className="bg-[#B8E6D5] hover:bg-[#A0D6C5] text-gray-900"
            data-testid="add-appointment-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Appointment
          </Button>
        </div>
      </div>

      {appointments.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
          <CalendarDays className="h-12 w-12 mx-auto text-[#B8E6D5] mb-4" />
          <h4 className="text-lg font-medium text-gray-900 mb-2">No appointments scheduled</h4>
          <p className="text-gray-600 mb-4">
            Keep track of doctor visits, medication refills, and vaccinations
          </p>
          <Button
            onClick={() => setDialogOpen(true)}
            className="bg-[#B8E6D5] hover:bg-[#A0D6C5] text-gray-900"
          >
            <Plus className="h-4 w-4 mr-2" />
            Schedule Appointment
          </Button>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Overdue */}
          {groupedAppointments.overdue.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-red-600 mb-3 flex items-center gap-2">
                <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                Overdue ({groupedAppointments.overdue.length})
              </h4>
              <div className="space-y-3">
                {groupedAppointments.overdue.map(appt => (
                  <AppointmentCard key={appt.id} appointment={appt} />
                ))}
              </div>
            </div>
          )}
          
          {/* Upcoming (Next 7 days) */}
          {groupedAppointments.upcoming.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-[#2E7D6A] mb-3 flex items-center gap-2">
                <span className="w-2 h-2 bg-[#2E7D6A] rounded-full"></span>
                Upcoming - Next 7 Days ({groupedAppointments.upcoming.length})
              </h4>
              <div className="space-y-3">
                {groupedAppointments.upcoming.map(appt => (
                  <AppointmentCard key={appt.id} appointment={appt} />
                ))}
              </div>
            </div>
          )}
          
          {/* Later */}
          {groupedAppointments.later.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-600 mb-3 flex items-center gap-2">
                <span className="w-2 h-2 bg-gray-400 rounded-full"></span>
                Later ({groupedAppointments.later.length})
              </h4>
              <div className="space-y-3">
                {groupedAppointments.later.map(appt => (
                  <AppointmentCard key={appt.id} appointment={appt} />
                ))}
              </div>
            </div>
          )}
          
          {/* Completed */}
          {showCompleted && groupedAppointments.completed.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-400 mb-3 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" />
                Completed ({groupedAppointments.completed.length})
              </h4>
              <div className="space-y-3">
                {groupedAppointments.completed.map(appt => (
                  <AppointmentCard key={appt.id} appointment={appt} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <AddAppointmentDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSuccess={() => {
          fetchAppointments();
          setDialogOpen(false);
        }}
      />
    </>
  );
};

export default AppointmentsTab;
